import { PlayerRef } from "@remotion/player";
import { useEffect, useRef } from "react";
import { useCurrentPlayerFrame } from "../hooks/useCurrentPlayerFrame";

interface TimelineScrubberProps {
  playerRef: React.RefObject<PlayerRef>;
  scrollPosition: number;
  visibleDuration: number;
  onFrameChange: (frame: number) => void;
  timelineRef: React.RefObject<HTMLDivElement>;
  videoDuration: number;
  zoom: number;
}

export function TimelineScrubber({
  playerRef,
  scrollPosition,
  visibleDuration,
  onFrameChange,
  timelineRef,
  videoDuration,
  zoom,
}: TimelineScrubberProps) {
  const currentFrame = useCurrentPlayerFrame(playerRef);
  const currentTime = currentFrame / 30;
  const prevTimeRef = useRef(currentTime);
  const userScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    onFrameChange(currentFrame);
  }, [currentFrame, onFrameChange]);

  // Track user scrolling
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;

    const handleScroll = () => {
      userScrollingRef.current = true;
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        userScrollingRef.current = false;
      }, 300);
    };

    timeline.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      timeline.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [timelineRef]);

  // Auto-scroll to keep scrubber in view
  useEffect(() => {
    if (!timelineRef.current || !videoDuration || videoDuration === 0) return;

    const timeline = timelineRef.current;
    const viewportWidth = timeline.clientWidth;
    const totalScrollWidth = timeline.scrollWidth;
    const maxScrollLeft = totalScrollWidth - viewportWidth;

    // Check if video is playing
    const timeDelta = Math.abs(currentTime - prevTimeRef.current);
    const isPlaying = timeDelta > 0.001 && timeDelta < 1;

    // Don't auto-scroll if user is manually scrolling
    if (!isPlaying || userScrollingRef.current || maxScrollLeft <= 0) {
      prevTimeRef.current = currentTime;
      return;
    }

    // Calculate scrubber position in pixels relative to the scrollable content
    const scrubberPositionPx = (currentTime / videoDuration) * totalScrollWidth;
    const currentScrollLeft = timeline.scrollLeft;

    let needsScroll = false;
    let targetScrollLeft = currentScrollLeft;

    // Paging logic: jump when scrubber leaves the visible view
    if (scrubberPositionPx > currentScrollLeft + viewportWidth) {
      // Scrubber moved past the right edge
      targetScrollLeft = Math.min(maxScrollLeft, scrubberPositionPx);
      needsScroll = true;
    } else if (scrubberPositionPx < currentScrollLeft) {
      // Scrubber is behind the left edge (e.g. seeking backwards)
      targetScrollLeft = Math.max(0, scrubberPositionPx);
      needsScroll = true;
    }

    if (needsScroll) {
      timeline.scrollTo({
        left: targetScrollLeft,
        behavior: "auto", // Jumps feel more precise for paging than smooth scrolling
      });
    }

    prevTimeRef.current = currentTime;
  }, [currentTime, visibleDuration, videoDuration, timelineRef]);

  // Calculate scrubber position as percentage of the TOTAL timeline width (not visible width)
  const scrubberLeftPosition = (currentTime / videoDuration) * 100;

  return (
    <div
      className="absolute top-0 w-0.5 h-full bg-indigo-600 z-50 pointer-events-none"
      style={{
        left: `${scrubberLeftPosition}%`,
      }}
    >
      {/* Scrubber head */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-8 bg-indigo-600 rounded-b-lg shadow-xl shadow-indigo-200 flex items-center justify-center">
        <div className="w-0.5 h-3 bg-white/40 rounded-full" />
      </div>
    </div>
  );
}
